/**
 * The seams between Paper's workspace shell and Visitors.
 *
 * Paper's shell pieces (the theme toggle, the profile panel's appearance
 * rows, the accent, the rail, ⌘K, the location scope, the panels) keep no
 * data and make no API call. Each reads the app through an adapter; this file
 * supplies them (Paper docs/workspace-shell.md, docs/accent.md, docs/panels.md).
 *
 * A preview session (`/preview`) gets in-memory stores throughout, so a
 * design review never changes the reviewer's own theme, accent, fold or
 * recents for the real app on the same origin.
 */

import { inject, type Provider } from '@angular/core';
import {
  PaperThemeService,
  paperLocalAccentStorage,
  paperMemoryAccentStorage,
  providePaperAccentStorage,
} from '@ricoh-paper/angular';
import {
  PAPER_SCOPE_ALL,
  paperLocalCommandRecents,
  paperLocalRailState,
  paperLocalThemeMemory,
  paperMemoryCommandRecents,
  paperMemoryRailState,
  paperMemoryThemeMemory,
  paperSomeScope,
  providePaperCommandRecents,
  providePaperLocationScopeAdapter,
  providePaperThemeMemory,
  providePaperWorkspaceRailState,
  type PaperLocationScopeAdapter,
} from '@ricoh-paper/angular/shell';
import {
  paperLocalPanelWidthStorage,
  paperMemoryPanelWidthStorage,
  providePaperPanelSurface,
  providePaperPanelWidthStorage,
} from '@ricoh-paper/angular/panels';

import { PreviewThemeService } from '../preview/preview-theme';
import { LocationsService } from '../services/locations.service';
import { LocationState } from '../state/location.state';

const PREFIX = 'rdx-visitors.';

export function paperShellProviders(preview: boolean): Provider[] {
  return [
    // In a preview, the theme follows ?theme= and is never written to storage.
    ...(preview
      ? [PreviewThemeService, { provide: PaperThemeService, useExisting: PreviewThemeService }]
      : []),
    providePaperThemeMemory(() =>
      preview ? paperMemoryThemeMemory() : paperLocalThemeMemory(`${PREFIX}theme-remembered`),
    ),
    // The accent (Ink by default; a colour; the division) is this browser's
    // choice. Visitors has no profile sync yet; see AGENTS.md.
    providePaperAccentStorage(() =>
      preview ? paperMemoryAccentStorage() : paperLocalAccentStorage(`${PREFIX}accent`),
    ),
    providePaperWorkspaceRailState(() =>
      preview ? paperMemoryRailState() : paperLocalRailState(`${PREFIX}rail:`),
    ),
    providePaperCommandRecents(() =>
      preview ? paperMemoryCommandRecents() : paperLocalCommandRecents(`${PREFIX}command-recents`),
    ),
    providePaperPanelWidthStorage(() =>
      preview
        ? paperMemoryPanelWidthStorage()
        : paperLocalPanelWidthStorage(`${PREFIX}panel-width`),
    ),
    // A push panel belongs to the page that opened it: a different path is a
    // different surface and the panel closes. Paper's default is the first
    // path segment, which is `preview` for every preview screen.
    providePaperPanelSurface(visitorsPanelSurface),
    providePaperLocationScopeAdapter(() => visitorsLocationScopeAdapter()),
  ];
}

/** The panel surface: the path, without its query or fragment. */
export function visitorsPanelSurface(url: string): string {
  return url.split(/[?#]/)[0] || '/';
}

/**
 * The nav's location scope over the app's own `LocationState`, which the
 * visit pages read. Visitors picks one site or all of them; the address book
 * ignores it (people are customer-wide).
 */
export function visitorsLocationScopeAdapter(): PaperLocationScopeAdapter {
  const state = inject(LocationState);
  const locations = inject(LocationsService);
  return {
    async loadOptions() {
      await locations.ensureLoaded();
      return { locations: locations.locations().map((l) => ({ id: l._id, label: l.name })) };
    },
    read() {
      const selected = state.selected();
      return selected ? paperSomeScope({ locationIds: [selected._id] }) : PAPER_SCOPE_ALL;
    },
    write(scope) {
      if (scope.kind === 'all' || !scope.locationIds.length) {
        state.select(null);
        return;
      }
      const id = scope.locationIds[0];
      const match = locations.locations().find((l) => l._id === id);
      state.select(match ? { _id: match._id, name: match.name } : null);
    },
  };
}
